<script setup>
import { useShopify } from '@/stores/shopify'
import LoadingIndicator from '../UI/LoadingIndicator.vue'
import CartLine from './CartLine.vue'
import ProductsRelated from './ProductsRelated.vue'
import PriceDisplay from './PriceDisplay.vue'
import eventBus from '@/eventBus'

import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import IconShoppingCartClear from '@/components/icons/IconShoppingCartClear.vue'
import ButtonDefault from '../atoms/ButtonDefault.vue'
import IconInformation from '../icons/IconInformation.vue'
import SideSheet from '../SideSheet.vue'
import { useRouter } from 'vue-router'
import { useCart } from '@/router/cart'
import MainFooter from '../MainFooter.vue'
import { onClickOutside } from '@vueuse/core'

const shopify = useShopify()
const router = useRouter()

const { isCartOpen, closeCart } = useCart()
const loading = ref(false)
const cartData = ref(null)
const products = ref([])
const cartPanel = ref(null)

const open = ref(false)

const randomCartItemId = ref(null);

onMounted(() => {
  eventBus.$on('cart-added-items', () => {
    reloadCart()
  })
})

onBeforeUnmount(() => {
  eventBus.$off('cart-added-items')
})

const reloadCart = () => {
  if (!shopify.cartId) {
    return;
  }

  loading.value = true
  shopify
    .fetchCart(shopify.cartId)
    .then((result) => {
      cartData.value = result
      shopify.updateCart(result.cart)

      if (cartData.value.cart.lines.edges.length > 0) {
        const randomIndex = Math.floor(Math.random() * cartData.value.cart.lines.edges.length);
        randomCartItemId.value = cartData.value.cart.lines.edges[randomIndex].node.merchandise.product.id;
      };
    })
    .finally(() => {
      loading.value = false
    })
}

const changeQuantity = (lineId, quantity) => {
  const oldCart = { ...cartData.value }
  loading.value = true

  shopify
    .changeLineQuantity(lineId, quantity)
    .then((result) => {
      cartData.value = result.cartLinesUpdate
      shopify.updateCart(result.cartLinesUpdate.cart)
    })
    .catch(() => {
      cartData.value = oldCart
    })
    .finally(() => {
      loading.value = false
    })
}

const removeLine = (lineId) => {
  loading.value = true
  shopify
    .removeLineFromCart(lineId)
    .then((result) => {
      cartData.value = result.cartLinesRemove
      shopify.updateCart(result.cartLinesRemove.cart)
    })
    .finally(() => {
      loading.value = false
    })
}

const trackCheckout = () => {
  // Create TikTok Tracking Object from Shopify Cart
  const tiktokCart = {
    contents: cartData.value.cart.lines.edges.map((line) => {
      return {
        content_id: line.node.merchandise.product.id,
        content_name: line.node.merchandise.product.title,
        content_type: "product"
      };
    }),
    value: cartData.value.cart.cost.totalAmount.amount,
    currency: "EUR"
  }
  window.ttq && window.ttq.track('InitiateCheckout', tiktokCart);
  window.trackEvent && window.trackEvent('checkout', cartData.value.cart.cost.totalAmount.amount);
  
  // Google Analytics tracking for checkout event
  if (window.gtag) {
    const items = cartData.value.cart.lines.edges.map((line) => {
      return {
        item_id: line.node.merchandise.product.id,
        item_name: line.node.merchandise.product.title,
        price: line.node.merchandise.price.amount,
        quantity: line.node.quantity,
        currency: "EUR"
      };
    });
    
    window.gtag('event', 'begin_checkout', {
      currency: "EUR",
      value: cartData.value.cart.cost.totalAmount.amount,
      items: items
    });
  }
}

router.afterEach(() => {
  open.value = isCartOpen()
  if (open.value == true) {
    reloadCart()
  }
})

watch(open, (state) => {
  if (state == false) {
    closeCart()
  }
})

onClickOutside(cartPanel, () => {
  closeCart()
})
</script>

<template>
  <SideSheet title="Warenkorb" v-model="open" :z-index="1010" :raw="true" icon-only ref="cartPanel">
    <LoadingIndicator v-if="loading" class="cart__loading-indicator" />

    <div v-if="cartData && cartData.cart.lines.edges.length > 0" class="cart__lines">
      <template v-for="line in cartData.cart.lines.edges" :key="line.node.id + line.node.quantity">
        <CartLine :line="line.node" @changeQuantity="changeQuantity" @removeLine="removeLine"  />
      </template>

      <div class="cart__info">
        <IconInformation />
        <span>Artikel im Warenkorb werden nicht reserviert.</span>
      </div>

      <div v-if="randomCartItemId" class="cart__related-products">
        <ProductsRelated
          :productId="randomCartItemId"
          :show-influencer="true"
          headline="Das könnte dir gefallen"
        />
      </div>

      <div class="cart__footer">
        <div class="cart__price">
          <span class="cart__price-total font-l1-bold">Gesamtsumme</span>
          <div class="cart__price-vat font-l3">inkl. MwSt.</div>
          <PriceDisplay
            :price="cartData.cart.cost.totalAmount"
            :font-size="1.25"
            :font-weight="700"
          />
        </div>
        <ButtonDefault type="dark" @click="trackCheckout" :href="cartData.cart.checkoutUrl" class="cart__footer-btn">
          Zur Kasse
        </ButtonDefault>
      </div>
    </div>
    <div v-else>
      <template v-if="!loading">
        <div class="cart__empty">
          <IconShoppingCartClear class="cart__empty-icon" />
          <p class="cart__empty-text">Dein Warenkorb ist leer</p>
          <ButtonDefault type="dark" :to="{ name: 'ProductListView' }">Weiter einkaufen</ButtonDefault>
        </div>
        <div class="cart__related-products">
          <ProductsRelated
            :products="products"
            headline="Das könnte dir gefallen"
            :show-influencer="true"
          />
        </div>
      </template>
    </div>
    <MainFooter />
  </SideSheet>
</template>

<style lang="scss" scoped>

.cart-line__wrapper {
  margin: 12px 12px;
  padding: 0 12px;
  background: white;
  border-radius: 20px;
}

.cart {

  &__info {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    margin-block: 40px;
    font-size: 14px;
    line-height: 1.2;
    padding: 0 12px;

    svg {
      flex-shrink: 0;
    }
  }

  &__footer {
    display: flex;
    padding: 24px 20px 48px;

    flex-direction: column;
    background: white; /*var(--clr-brand-50);*/

    position: sticky;
    bottom: 0;
  }

  &__footer-btn {
    margin-top: 24px;
    width: 100%;
  }

  &__price {
    display: flex;
    gap: 4px;
    align-items: flex-end;
  }

  &__price-vat {
    color: var(--clr-gray-500);
    margin-right: auto;
    padding-bottom: 0.125em;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 20px 64px;

    .button {
      max-width: 350px;
      width: 100%;
    }
  }

  &__empty-icon {
    margin-block: 40px 75px;
  }

  &__empty-text {
    font-weight: 600;
    margin-bottom: 25px;
  }

  &__related-products {
    margin-bottom: 40px;
  }

  &__loading-indicator {
    position: absolute;
    background-color: rgba(255, 255, 2545, 0.8);
    z-index: +10;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.cart__lines {
  background: var(--clr-brand-50);
  padding-top: 1px;
}

.related-products {
  background: var(--clr-brand-50);
}

</style>
